import {
  FINANCIAL_SCHEMA_VERSION,
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
  isValidStatusForType,
  isValidTransactionType,
} from './constants';
import { getFinancialDate, isValidCivilDate } from './dates';
import { getAmountCents } from './money';
import { calculateFinancialSummary } from './summary';

const KNOWN_FIELDS = new Set([
  'id',
  'nome',
  'valor',
  'data',
  'categoria',
  'tipo',
  'status',
  'amountCents',
  'date',
  'schemaVersion',
]);

const KNOWN_STATUSES = new Set(Object.values(TRANSACTION_STATUS));
const hasOwn = (record, field) =>
  Object.prototype.hasOwnProperty.call(record, field);

const emptyCounts = () => ({
  total: 0,
  valid: 0,
  invalid: 0,
  invalidAmounts: 0,
  invalidDates: 0,
  invalidTypes: 0,
  invalidStatuses: 0,
  incompatibleTypeStatus: 0,
  sourceTypeMismatches: 0,
  legacyValueOnly: 0,
  legacyWithoutSchemaVersion: 0,
  validSchemaV2: 0,
  incompleteSchemaV2: 0,
  unknownSchemaVersion: 0,
  amountCentsLegacyMismatches: 0,
  dateLegacyMismatches: 0,
  amountCentsPresent: 0,
  invalidAmountCentsWithLegacyFallback: 0,
  withUnknownFields: 0,
  withMissingExpectedFields: 0,
});

const getMissingExpectedFields = (record) => {
  const missing = ['id', 'nome', 'categoria', 'tipo', 'status'].filter(
    (field) => !hasOwn(record, field)
  );
  if (!hasOwn(record, 'valor') && !hasOwn(record, 'amountCents')) {
    missing.push('valor|amountCents');
  }
  if (!hasOwn(record, 'data') && !hasOwn(record, 'date')) {
    missing.push('data|date');
  }
  return missing;
};

/**
 * Audita registros financeiros sem alterá-los e sem incluir descrição ou valor
 * no relatório. `valid` representa o contrato financeiro mínimo; campos extras
 * e campos descritivos ausentes continuam sendo reportados como avisos.
 */
export const analyzeFinancialRecords = ({ receitas = [], despesas = [] } = {}) => {
  const counts = emptyCounts();
  const problematicRecords = [];

  const inspect = (rawRecord, source, expectedType) => {
    const record = rawRecord && typeof rawRecord === 'object' ? rawRecord : {};
    const problems = [];
    const amountCents = getAmountCents(record);
    const financialDate = getFinancialDate(record);
    const validType = isValidTransactionType(record.tipo);
    const knownStatus = KNOWN_STATUSES.has(record.status);
    const validStatus = validType && isValidStatusForType(record.tipo, record.status);
    const sourceMatchesType = validType && record.tipo === expectedType;
    const hasAmountCents = hasOwn(record, 'amountCents');
    const validStoredAmountCents =
      Number.isSafeInteger(record.amountCents) && record.amountCents >= 0;
    const hasSchemaVersion = hasOwn(record, 'schemaVersion');
    const isSchemaV2 = record.schemaVersion === FINANCIAL_SCHEMA_VERSION;
    const validStoredDate = isValidCivilDate(record.date);
    const legacyAmountCents = hasOwn(record, 'valor')
      ? getAmountCents({ valor: record.valor })
      : null;
    const legacyDate = hasOwn(record, 'data')
      ? getFinancialDate({ data: record.data })
      : null;
    const amountMismatch =
      validStoredAmountCents &&
      legacyAmountCents !== null &&
      record.amountCents !== legacyAmountCents;
    const dateMismatch =
      validStoredDate && legacyDate !== null && record.date !== legacyDate;
    const completeSchemaV2 = validStoredAmountCents && validStoredDate;
    let schemaIsValid = true;

    counts.total += 1;

    if (hasOwn(record, 'valor') && !hasAmountCents) counts.legacyValueOnly += 1;
    if (hasAmountCents) counts.amountCentsPresent += 1;

    if (amountCents === null) {
      counts.invalidAmounts += 1;
      problems.push({ code: 'INVALID_AMOUNT', fields: ['amountCents', 'valor'] });
    } else if (hasAmountCents && !validStoredAmountCents) {
      counts.invalidAmountCentsWithLegacyFallback += 1;
      problems.push({
        code: 'INVALID_AMOUNT_CENTS_USING_LEGACY_FALLBACK',
        fields: ['amountCents', 'valor'],
      });
    }

    if (financialDate === null) {
      counts.invalidDates += 1;
      problems.push({ code: 'INVALID_DATE', fields: ['date', 'data'] });
    }

    if (!hasSchemaVersion) {
      counts.legacyWithoutSchemaVersion += 1;
    } else if (!isSchemaV2) {
      counts.unknownSchemaVersion += 1;
      schemaIsValid = false;
      problems.push({ code: 'UNKNOWN_SCHEMA_VERSION', fields: ['schemaVersion'] });
    } else if (!completeSchemaV2) {
      const incompleteFields = [];
      if (!validStoredAmountCents) incompleteFields.push('amountCents');
      if (!validStoredDate) incompleteFields.push('date');
      counts.incompleteSchemaV2 += 1;
      schemaIsValid = false;
      problems.push({
        code: 'INCOMPLETE_SCHEMA_V2',
        fields: incompleteFields,
      });
    }

    if (amountMismatch) {
      counts.amountCentsLegacyMismatches += 1;
      schemaIsValid = false;
      problems.push({
        code: 'AMOUNT_CENTS_LEGACY_MISMATCH',
        fields: ['amountCents', 'valor'],
      });
    }

    if (dateMismatch) {
      counts.dateLegacyMismatches += 1;
      schemaIsValid = false;
      problems.push({
        code: 'DATE_LEGACY_MISMATCH',
        fields: ['date', 'data'],
      });
    }

    if (isSchemaV2 && completeSchemaV2 && !amountMismatch && !dateMismatch) {
      counts.validSchemaV2 += 1;
    }
    if (!validType) {
      counts.invalidTypes += 1;
      problems.push({ code: 'INVALID_TYPE', fields: ['tipo'] });
    }

    if (!knownStatus) {
      counts.invalidStatuses += 1;
      problems.push({ code: 'INVALID_STATUS', fields: ['status'] });
    } else if (validType && !validStatus) {
      counts.invalidStatuses += 1;
      counts.incompatibleTypeStatus += 1;
      problems.push({
        code: 'INCOMPATIBLE_TYPE_STATUS',
        fields: ['tipo', 'status'],
      });
    }

    if (validType && !sourceMatchesType) {
      counts.sourceTypeMismatches += 1;
      problems.push({ code: 'SOURCE_TYPE_MISMATCH', fields: ['tipo'] });
    }

    const unknownFields = Object.keys(record).filter(
      (field) => !KNOWN_FIELDS.has(field)
    );
    if (unknownFields.length > 0) {
      counts.withUnknownFields += 1;
      problems.push({ code: 'UNKNOWN_FIELDS', fields: unknownFields.sort() });
    }

    const missingFields = getMissingExpectedFields(record);
    if (missingFields.length > 0) {
      counts.withMissingExpectedFields += 1;
      problems.push({ code: 'MISSING_EXPECTED_FIELDS', fields: missingFields.sort() });
    }

    const isValid =
      amountCents !== null &&
      financialDate !== null &&
      validType &&
      validStatus &&
      sourceMatchesType &&
      schemaIsValid;

    if (isValid) counts.valid += 1;
    else counts.invalid += 1;

    if (problems.length > 0) {
      problematicRecords.push({
        id: record.id ?? null,
        source,
        problems,
      });
    }
  };

  receitas.forEach((record) =>
    inspect(record, 'receitas', TRANSACTION_TYPES.INCOME)
  );
  despesas.forEach((record) =>
    inspect(record, 'despesas', TRANSACTION_TYPES.EXPENSE)
  );

  return {
    counts,
    summary: calculateFinancialSummary({ receitas, despesas }),
    problematicRecords,
  };
};
